import React from 'react';
import { PageTransition } from '../transitions/PageTransition';
import { Santri } from '../../types';

interface PortalWaliProfilInfoProps {
  santri: Santri;
  onBack: () => void;
}

export const PortalWaliProfilInfo: React.FC<PortalWaliProfilInfoProps> = ({ santri, onBack }) => {
  return (
    <PageTransition>
      <div className="min-h-screen bg-[#f6f8f6] text-[#111811] font-sans antialiased pb-32">
        <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden pb-32">
          <div className="bg-white rounded-b-3xl shadow-sm pb-6">
            <div className="flex items-center justify-between p-4 pt-6">
              <button
                onClick={onBack}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f6f8f6] text-[#111811] transition hover:bg-gray-200"
              >
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
              <h2 className="text-xl font-bold tracking-tight text-[#111811]">Profil Santri</h2>
              <div className="h-10 w-10"></div>
            </div>
            <div className="px-4 mt-2">
              <div className="flex flex-col rounded-2xl bg-[#1B3C53] p-6 text-white shadow-lg relative overflow-hidden group items-center text-center">
                <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-[#40513B] opacity-20 blur-2xl"></div>
                <div className="absolute -left-6 -bottom-6 h-32 w-32 rounded-full bg-[#40513B] opacity-10 blur-2xl"></div>
                <div className="relative z-10 mb-4">
                  <div
                    className="bg-center bg-no-repeat bg-cover rounded-full h-24 w-24 border-4 border-[#40513B] shadow-md"
                    style={{backgroundImage: `url("${santri.foto || 'https://placehold.co/150x150'}")`}}
                  ></div>
                  <button className="absolute bottom-0 right-0 bg-white text-[#40513B] p-1.5 rounded-full shadow-sm border border-gray-100">
                    <span className="material-symbols-outlined text-sm font-bold">photo_camera</span>
                  </button>
                </div>
                <div className="z-10 w-full">
                  <h3 className="text-xl font-bold leading-tight mb-1 text-white">{santri.nama}</h3>
                  <p className="text-white/80 text-sm font-medium">Kelas {santri.kelas} - Wali Kelas: {santri.waliKelas || '-'}</p>
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <span className="bg-white/10 px-3 py-1 rounded-full text-xs text-white border border-white/10">NIS: {santri.nis || '-'}</span>
                    <span className="bg-green-500/20 px-3 py-1 rounded-full text-xs text-white border border-white/20">Santri Aktif</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-4 mt-6">
              <button className="w-full flex items-center justify-center gap-2 rounded-xl bg-white border border-gray-200 text-[#111811] px-4 py-3 shadow-sm hover:bg-gray-50 active:scale-95 transition-all group">
                <span className="material-symbols-outlined text-[#40513B] group-hover:scale-110 transition-transform">edit_square</span>
                <span className="font-bold text-sm">Edit Data Profil</span>
              </button>
            </div>
          </div>

          <div className="px-4 mt-6 space-y-5">
            {/* Data Pribadi */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-50 text-green-600">
                  <span className="material-symbols-outlined text-lg">person</span>
                </div>
                <h3 className="font-bold text-lg text-[#111811]">Data Pribadi</h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nama Lengkap</p>
                  <p className="text-base font-semibold text-[#111811]">{santri.nama}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Jenis Kelamin</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.jenisKelamin || '-'}</p>
                  </div>
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Umur</p>
                    <p className="text-base font-semibold text-[#111811]">
                        {santri.tanggalLahir ? `${new Date().getFullYear() - new Date(santri.tanggalLahir).getFullYear()} Tahun` : '-'}
                    </p>
                  </div>
                </div>
                <div className="gap-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tempat, Tanggal Lahir</p>
                  <p className="text-base font-semibold text-[#111811]">
                    {santri.tempatLahir ? `${santri.tempatLahir}, ` : ''}{santri.tanggalLahir || '-'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Anak Ke-</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.anakKe || '-'}</p>
                  </div>
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Jml Saudara</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.jumlahSaudara || '-'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">NIK</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.nik || '-'}</p>
                  </div>
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">No. KK</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.noKk || '-'}</p>
                  </div>
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">No. Reg Akta</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.noAkte || '-'}</p>
                  </div>
                </div>
                <div className="gap-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Alamat Lengkap</p>
                  <p className="text-base font-medium text-[#111811] leading-relaxed">
                    {santri.alamatJalan || ''}
                    {santri.rt ? `, RT ${santri.rt}` : ''}
                    {santri.rw ? ` RW ${santri.rw}` : ''}
                    {santri.desaKelurahan ? `, Kel. ${santri.desaKelurahan}` : ''}
                    {santri.kecamatan ? `, Kec. ${santri.kecamatan}` : ''}
                    {santri.kabupatenKota ? `, ${santri.kabupatenKota}` : ''}
                    {santri.provinsi ? `, ${santri.provinsi}` : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Akademik & Ma'had */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <span className="material-symbols-outlined text-lg">school</span>
                </div>
                <h3 className="font-bold text-lg text-[#111811]">Akademik &amp; Ma'had</h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Kelas</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.kelas}</p>
                  </div>
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Asrama</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.asrama}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">NIS</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.nis || '-'}</p>
                  </div>
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">NISN</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.nisn || '-'}</p>
                  </div>
                </div>
                <div className="gap-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Wali Kelas</p>
                  <div className="flex flex-col gap-1">
                    <p className="text-base font-semibold text-[#111811]">{santri.waliKelas || '-'}</p>
                    {/* Phone number integration could be added if available in data */}
                  </div>
                </div>
                <div className="gap-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Murobbi</p>
                  <div className="flex flex-col gap-1">
                    <p className="text-base font-semibold text-[#111811]">{santri.murobbi || '-'}</p>
                  </div>
                </div>
                <div className="gap-1 mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Asal Sekolah SD</p>
                  <p className="text-base font-semibold text-[#111811]">{santri.asalSekolahSD || '-'}</p>
                </div>
              </div>
            </div>

            {/* Data Fisik */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-red-600">
                  <span className="material-symbols-outlined text-lg">medical_services</span>
                </div>
                <h3 className="font-bold text-lg text-[#111811]">Data Fisik</h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Berat Badan</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.beratBadan ? `${santri.beratBadan} kg` : '-'}</p>
                  </div>
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tinggi Badan</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.tinggiBadan ? `${santri.tinggiBadan} cm` : '-'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Gol. Darah</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.golonganDarah || '-'}</p>
                  </div>
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ukuran Baju</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.ukuranBaju || '-'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ukuran Sepatu</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.ukuranSepatu || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Minat & Cita-cita */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-50 text-purple-600">
                  <span className="material-symbols-outlined text-lg">star</span>
                </div>
                <h3 className="font-bold text-lg text-[#111811]">Minat &amp; Cita-cita</h3>
              </div>
              <div className="space-y-4">
                <div className="gap-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hobi</p>
                  <p className="text-base font-semibold text-[#111811]">{santri.hobby || '-'}</p>
                </div>
                <div className="gap-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cita-cita</p>
                  <p className="text-base font-semibold text-[#111811]">{santri.citaCita || '-'}</p>
                </div>
              </div>
            </div>

            {/* Data Orang Tua */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-50 text-orange-600">
                  <span className="material-symbols-outlined text-lg">family_restroom</span>
                </div>
                <h3 className="font-bold text-lg text-[#111811]">Data Orang Tua</h3>
              </div>
              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#40513B] rounded-full"></span> Data Ayah
                </h4>
                <div className="space-y-4 pl-3.5 border-l-2 border-gray-100">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nama Ayah</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.namaAyah || '-'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="gap-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tgl Lahir</p>
                      <p className="text-base font-semibold text-[#111811]">{santri.tanggalLahirAyah || '-'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="gap-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pekerjaan</p>
                      <p className="text-base font-semibold text-[#111811]">{santri.pekerjaanAyah || '-'}</p>
                    </div>
                    <div className="gap-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pendidikan</p>
                      <p className="text-base font-semibold text-[#111811]">{santri.pendidikanAyah || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-pink-500 rounded-full"></span> Data Ibu
                </h4>
                <div className="space-y-4 pl-3.5 border-l-2 border-gray-100">
                  <div className="gap-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nama Ibu</p>
                    <p className="text-base font-semibold text-[#111811]">{santri.namaIbu || '-'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="gap-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tgl Lahir</p>
                      <p className="text-base font-semibold text-[#111811]">{santri.tanggalLahirIbu || '-'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="gap-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pekerjaan</p>
                      <p className="text-base font-semibold text-[#111811]">{santri.pekerjaanIbu || '-'}</p>
                    </div>
                    <div className="gap-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pendidikan</p>
                      <p className="text-base font-semibold text-[#111811]">{santri.pendidikanIbu || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#40513B] text-white px-4 py-4 shadow-lg shadow-green-500/20 transition-all hover:bg-primary-dark hover:text-white active:scale-95 mb-4 group">
              <span className="material-symbols-outlined group-hover:scale-110 transition-transform">edit_square</span>
              <span className="font-bold text-base">Edit Data Profil</span>
            </button>
            <div className="h-6"></div>
          </div>

          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
            <button
              onClick={onBack}
              className="flex items-center justify-center gap-2 rounded-full bg-[#111811] text-white px-6 py-3 shadow-xl transition-all hover:bg-gray-800 active:scale-95"
            >
              <span className="material-symbols-outlined text-lg">arrow_back</span>
              <span className="font-bold text-sm">Kembali</span>
            </button>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};
